import { createContext, useContext, useMemo } from "react";
import Session from "supertokens-web-js/recipe/session";

import type { AccessTokenPayload } from "@/helpers/jwt";
import { parseAccessTokenPayload } from "@/helpers/jwt";

interface AccessTokenContext {
  getAccessToken: () => Promise<string | undefined>;
  getAccessTokenPayload: () => Promise<AccessTokenPayload | undefined>;
}

const AccessTokenContext = createContext<AccessTokenContext>(null!);

export function useAccessTokenContext(): AccessTokenContext {
  return useContext(AccessTokenContext);
}

export function SuperTokensAccessTokenProvider({ children }: { children: React.ReactNode }): React.ReactNode {
  const accessTokenContextValue = useMemo(
    () => ({
      getAccessToken: async () => {
        if (await Session.doesSessionExist()) {
          return Session.getAccessToken();
        }
      },
      getAccessTokenPayload: async () => {
        if (await Session.doesSessionExist()) {
          const accessToken = await Session.getAccessToken();

          if (accessToken) {
            return parseAccessTokenPayload(accessToken);
          }
        }
      },
    }),
    [],
  );

  return <AccessTokenContext value={accessTokenContextValue}>{children}</AccessTokenContext>;
}

export function RawAccessTokenProvider({
  token,
  children,
}: {
  token: string;
  children: React.ReactNode;
}): React.ReactNode {
  const accessTokenValueContext = useMemo(
    () => ({
      getAccessToken: () => Promise.resolve(token),
      getAccessTokenPayload: () => Promise.resolve(parseAccessTokenPayload(token)),
    }),
    [token],
  );

  return <AccessTokenContext value={accessTokenValueContext}>{children}</AccessTokenContext>;
}
